import React, { MouseEvent, useMemo, ReactElement } from "react";

import clsx from "clsx";
import { Guid } from "guid-typescript";

import { Popover } from "@plex/react-components";

import { IValidationIssue, ValidationTag } from "infrastructure/validation";
import { LocalizableText } from "ui/components";

import styles from "./InlineEdit.module.scss";
import { InlineEditContext, IOpenEditPopupOptions } from "./InlineEditProvider";
import "./InlineEdit.scss";

export interface IInlineEditProps {
  className?: string;
  popupClassName?: string;
  changedClassName?: string;
  initialValue?: string;
  originalValue?: string;
  onValueChanged: (value?: string) => void;
  isChanged?: boolean;
  validationIssues?: IValidationIssue[];
  validationTags?: ValidationTag[];
  children?: React.ReactNode;
}

interface IInlineEditContainerProps extends IInlineEditProps {
  onEditPopupOpened?: (options: IOpenEditPopupOptions) => void;
  popupElement?: JSX.Element;
  onEditPopupClosed?: () => void;
  editMode: boolean;
  elementId: string;
  readOnly: boolean;
}

interface IInlineEditReadOnlyProps<T> extends IInlineEditProps {
  canEdit: (context: T) => boolean;
  context: T;
}

declare type InlineEditProps<T> = unknown extends T ? IInlineEditProps : IInlineEditReadOnlyProps<T>;

function isReadonlyProps<T>(props: IInlineEditProps | IInlineEditReadOnlyProps<T>): props is IInlineEditReadOnlyProps<T> {
  return "canEdit" in props && !!props.canEdit;
}

const ErrorContent: React.FC<{ content: ReactElement; issues: IValidationIssue[] }> = ({ content, issues }) => {
  return (
    <Popover anchor={content} placement="left" theme={"error"} distance={10} maxWidth="200">
      {issues.map((val, i) => (
        <div key={i}>
          <LocalizableText text={val.message} />
        </div>
      ))}
    </Popover>
  );
};

const getValidationIssues = (validationIssues?: IValidationIssue[], validationTags?: ValidationTag[]) => {
  if (validationIssues?.length && validationTags?.length) {
    return validationIssues.filter(i => i.tag === undefined || validationTags.includes(i.tag));
  }

  return validationIssues || [];
};

const InlineEditContainer: React.FC<IInlineEditContainerProps> = ({
  onEditPopupOpened,
  popupElement,
  onValueChanged,
  isChanged,
  initialValue,
  originalValue,
  className,
  popupClassName,
  changedClassName,
  elementId,
  editMode,
  readOnly,
  validationIssues,
  validationTags,
  children
}) => {
  const openPopup = () => {
    onEditPopupOpened?.({
      elementId: elementId,
      initialValue: initialValue,
      originalValue: originalValue,
      popupClassName: popupClassName,
      onValueChanged: onValueChanged
    });
  };

  const onClick = (e: MouseEvent) => {
    // to change state only edit component (for, example prevent selection table row if component is placed there)
    e.stopPropagation();
    openPopup();
  };

  const onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) {
      // track only left mouse button
      e.preventDefault();
    }
  };

  const issues = getValidationIssues(validationIssues, validationTags);
  const isError = issues.length > 0;
  const baseClasses = [styles.base, { [changedClassName || styles.changed]: isChanged && !editMode && !isError, [styles.errorMode]: isError }];

  const content = readOnly ? (
    <div className={clsx(baseClasses, className)}>{children}</div>
  ) : (
    <div
      className={clsx(baseClasses, { [styles.viewMode]: !editMode, [styles.editMode]: editMode }, className)}
      onClick={onClick}
      onFocus={openPopup}
      tabIndex={editMode ? -1 : 0}
      onMouseDown={onMouseDown}
    >
      {children}
      {editMode && popupElement}
    </div>
  );

  return isError ? <ErrorContent content={content} issues={issues || []} /> : content;
};

const MemorizedInlineEditContainer = React.memo(InlineEditContainer);

export function InlineEdit<T>({ children, ...props }: InlineEditProps<T>) {
  const elementId = useMemo(() => {
    return Guid.raw();
  }, []);

  const isReadOnly = isReadonlyProps(props) && !props.canEdit(props.context);

  return isReadOnly ? (
    <MemorizedInlineEditContainer {...props} elementId={elementId} editMode={false} readOnly>
      {children}
    </MemorizedInlineEditContainer>
  ) : (
    <InlineEditContext.Consumer>
      {ctx => {
        const editMode = elementId === ctx.currentElementId;
        return (
          <MemorizedInlineEditContainer
            {...props}
            elementId={elementId}
            editMode={editMode}
            onEditPopupOpened={ctx.onEditPopupOpened}
            popupElement={editMode ? ctx.popupElement : undefined}
            onEditPopupClosed={ctx.onEditPopupClosed}
            readOnly={false}
          >
            {children}
          </MemorizedInlineEditContainer>
        );
      }}
    </InlineEditContext.Consumer>
  );
}
